import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  API,
  authHeaders,
  authConfig,
  alertError,
  alertSuccess,
  btnPrimary,
  btnSecondary,
  fieldStyle,
  labelStyle,
  thStyle,
  tdStyle,
  pageTitleStyle,
  pageSubtitleStyle,
} from '../../api/ticketsApi';
import { formatUtcToIst } from '../../utils/datetime';

const TABS = [
  { key: 'domains', label: 'Domains' },
  { key: 'workflow', label: 'Workflow' },
  { key: 'templates', label: 'Notifications' },
  { key: 'audit', label: 'Audit Log' },
];

function CrudTable({ columns, rows, onEdit, onDelete, idKey = 'id' }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
        <thead>
          <tr style={{ background: '#F9FAFB', textAlign: 'left' }}>
            {columns.map((c) => (
              <th key={c.key} style={thStyle}>{c.label}</th>
            ))}
            <th style={thStyle}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row[idKey] || row.domain_id || row.category_id || row.service_id || row.routing_id || row.field_id} style={{ borderBottom: '1px solid #E5E7EB' }}>
              {columns.map((c) => (
                <td key={c.key} style={tdStyle}>
                  {c.render ? c.render(row) : row[c.key]}
                </td>
              ))}
              <td style={tdStyle}>
                <button type="button" onClick={() => onEdit(row)} style={{ ...btnSecondary, marginRight: '8px' }}>Edit</button>
                {onDelete && (
                  <button type="button" onClick={() => onDelete(row)} style={{ ...btnSecondary, color: '#B91C1C' }}>Delete</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DomainsTab() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ key: '', label: '', prefix: '', enabled: true });
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/master/domains`, authHeaders());
      setRows(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load domains');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    try {
      if (editingId) {
        await axios.patch(`${API}/master/domains/${editingId}`, {
          label: form.label,
          prefix: form.prefix,
          enabled: form.enabled,
        }, authHeaders());
        setSuccess('Domain updated');
      } else {
        await axios.post(`${API}/master/domains`, {
          key: form.key,
          label: form.label,
          prefix: form.prefix,
          enabled: form.enabled,
        }, authHeaders());
        setSuccess('Domain created');
      }
      setForm({ key: '', label: '', prefix: '', enabled: true });
      setEditingId(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Save failed');
    }
  };

  if (loading) return <p style={{ color: '#6B7280' }}>Loading…</p>;

  return (
    <div>
      {error && <div style={alertError}>{error}</div>}
      {success && <div style={alertSuccess}>{success}</div>}
      <form onSubmit={handleSubmit} style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr) auto', gap: '8px', marginBottom: '10px', padding: '10px', background: '#F9FAFB', borderRadius: '8px' }}>
        <div>
          <label style={labelStyle}>Key *</label>
          <input value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} required disabled={Boolean(editingId)} style={fieldStyle} placeholder="hardware" />
        </div>
        <div>
          <label style={labelStyle}>Label *</label>
          <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} required style={fieldStyle} placeholder="Hardware" />
        </div>
        <div>
          <label style={labelStyle}>Prefix *</label>
          <input value={form.prefix} onChange={(e) => setForm({ ...form, prefix: e.target.value })} required style={fieldStyle} placeholder="HW" />
        </div>
        <div>
          <label style={labelStyle}>Enabled</label>
          <select value={form.enabled ? 'yes' : 'no'} onChange={(e) => setForm({ ...form, enabled: e.target.value === 'yes' })} style={fieldStyle}>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
          <button type="submit" style={btnPrimary}>{editingId ? 'Update' : 'Add'}</button>
          {editingId && <button type="button" onClick={() => { setEditingId(null); setForm({ key: '', label: '', prefix: '', enabled: true }); }} style={btnSecondary}>Cancel</button>}
        </div>
      </form>
      <CrudTable
        columns={[
          { key: 'key', label: 'Key' },
          { key: 'label', label: 'Label' },
          { key: 'prefix', label: 'Prefix' },
          { key: 'enabled', label: 'Enabled', render: (r) => (r.enabled !== false ? 'Yes' : 'No') },
        ]}
        rows={rows}
        idKey="domain_id"
        onEdit={(r) => { setEditingId(r.domain_id); setForm({ key: r.key, label: r.label, prefix: r.prefix, enabled: r.enabled !== false }); }}
        onDelete={async (r) => {
          if (!window.confirm('Disable this domain?')) return;
          await axios.delete(`${API}/master/domains/${r.domain_id}`, authHeaders());
          await load();
        }}
      />
    </div>
  );
}

function WorkflowTab() {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    axios.get(`${API}/config/workflow`, authHeaders())
      .then((res) => setForm(res.data))
      .catch((err) => setError(err.response?.data?.detail || 'Failed to load workflow config'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    try {
      const res = await axios.patch(`${API}/config/workflow`, {
        closure_verification_required: form.closure_verification_required,
        auto_close_completed_hours: Number(form.auto_close_completed_hours),
        sla_scan_interval_minutes: Number(form.sla_scan_interval_minutes),
        sla_at_risk_hours: Number(form.sla_at_risk_hours),
      }, authHeaders());
      setForm(res.data);
      setSuccess('Workflow settings saved');
    } catch (err) {
      setError(err.response?.data?.detail || 'Save failed');
    }
  };

  if (loading || !form) return <p style={{ color: '#6B7280' }}>Loading workflow config…</p>;

  return (
    <div>
      {error && <div style={alertError}>{error}</div>}
      {success && <div style={alertSuccess}>{success}</div>}
      <form onSubmit={handleSave} style={{ maxWidth: '480px', display: 'grid', gap: '10px', padding: '10px', background: '#F9FAFB', borderRadius: '8px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
          <input type="checkbox" checked={form.closure_verification_required} onChange={(e) => setForm({ ...form, closure_verification_required: e.target.checked })} />
          Require closure verification before close
        </label>
        <div>
          <label style={labelStyle}>Auto-close completed tickets (hours)</label>
          <input type="number" min="0" value={form.auto_close_completed_hours} onChange={(e) => setForm({ ...form, auto_close_completed_hours: e.target.value })} style={fieldStyle} />
        </div>
        <div>
          <label style={labelStyle}>SLA scan interval (minutes)</label>
          <input type="number" min="1" value={form.sla_scan_interval_minutes} onChange={(e) => setForm({ ...form, sla_scan_interval_minutes: e.target.value })} style={fieldStyle} />
        </div>
        <div>
          <label style={labelStyle}>SLA at-risk window (hours before due)</label>
          <input type="number" min="1" value={form.sla_at_risk_hours} onChange={(e) => setForm({ ...form, sla_at_risk_hours: e.target.value })} style={fieldStyle} />
        </div>
        <button type="submit" style={{ ...btnPrimary, width: 'fit-content' }}>Save Workflow</button>
      </form>
    </div>
  );
}

function TemplatesTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = () => {
    setLoading(true);
    axios.get(`${API}/config/notification-templates`, authHeaders())
      .then((res) => setRows(res.data))
      .catch((err) => setError(err.response?.data?.detail || 'Failed to load templates'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const toggleEnabled = async (row) => {
    await axios.patch(`${API}/config/notification-templates/${row.template_id}`, { enabled: !row.enabled }, authHeaders());
    load();
  };

  if (loading) return <p style={{ color: '#6B7280' }}>Loading templates…</p>;

  return (
    <div>
      {error && <div style={alertError}>{error}</div>}
      <p style={{ color: '#6B7280', fontSize: '12px', marginBottom: '8px' }}>
        Placeholders: {'{{ticket_no}}'}, {'{{subject}}'}, {'{{status}}'}
      </p>
      <CrudTable
        columns={[
          { key: 'event', label: 'Event' },
          { key: 'channel', label: 'Channel' },
          { key: 'subject', label: 'Subject' },
          { key: 'enabled', label: 'Enabled', render: (r) => (r.enabled ? 'Yes' : 'No') },
        ]}
        rows={rows}
        idKey="template_id"
        onEdit={(r) => toggleEnabled(r)}
        onDelete={null}
      />
      <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '12px' }}>Click Edit to toggle enabled/disabled.</p>
    </div>
  );
}

function AuditTab() {
  const [rows, setRows] = useState([]);
  const [ticketNo, setTicketNo] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async (filter = ticketNo) => {
    setLoading(true);
    try {
      const params = filter ? { ticket_no: filter } : {};
      const res = await axios.get(`${API}/audit`, authConfig(params));
      setRows(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(''); }, []);

  return (
    <div>
      {error && <div style={alertError}>{error}</div>}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
        <input value={ticketNo} onChange={(e) => setTicketNo(e.target.value)} placeholder="Filter by ticket no…" style={{ ...fieldStyle, maxWidth: '240px' }} />
        <button type="button" onClick={() => load(ticketNo)} style={btnPrimary}>Search</button>
      </div>
      {loading ? <p style={{ color: '#6B7280' }}>Loading audit log…</p> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ background: '#F9FAFB', textAlign: 'left' }}>
                <th style={thStyle}>Time (IST)</th>
                <th style={thStyle}>Ticket</th>
                <th style={thStyle}>Action</th>
                <th style={thStyle}>Actor</th>
                <th style={thStyle}>Details</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.audit_id} style={{ borderBottom: '1px solid #E5E7EB' }}>
                  <td style={tdStyle}>{formatUtcToIst(r.timestamp)}</td>
                  <td style={tdStyle}>{r.entity_id}</td>
                  <td style={tdStyle}>{r.action}</td>
                  <td style={tdStyle}>{r.actor_name}</td>
                  <td style={tdStyle}><code style={{ fontSize: '12px' }}>{JSON.stringify(r.details)}</code></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TicketMasterAdminPage() {
  const [tab, setTab] = useState('domains');

  const tabStyle = (active) => ({
    padding: '6px 10px',
    border: 'none',
    borderBottom: active ? '2px solid #002FA7' : '2px solid transparent',
    background: 'transparent',
    color: active ? '#002FA7' : '#6B7280',
    fontWeight: active ? '600' : '500',
    cursor: 'pointer',
    fontSize: '12px',
  });

  return (
    <div>
      <h2 style={pageTitleStyle}>Ticket Master Admin</h2>
      <p style={pageSubtitleStyle}>
        Manage ticket domains, workflow settings, and notification templates. Domain-specific form fields are configured in Form Builder.
      </p>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '10px', borderBottom: '1px solid #E5E7EB' }}>
        {TABS.map((t) => (
          <button key={t.key} type="button" style={tabStyle(tab === t.key)} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'domains' && <DomainsTab />}
      {tab === 'workflow' && <WorkflowTab />}
      {tab === 'templates' && <TemplatesTab />}
      {tab === 'audit' && <AuditTab />}
    </div>
  );
}

export default TicketMasterAdminPage;
